/**
 * Advanced Tab
 *
 * Advanced target configuration with additional options.
 */

import { Row, Col, FormGroup, Label, Input } from 'reactstrap';
import type { CreateTargetRequest } from '../../types/emissions';
import { TargetFramework } from '../../types/emissions';

interface AdvancedTabProps {
  formData: CreateTargetRequest;
  onFormChange: (data: Partial<CreateTargetRequest>) => void;
}

const FRAMEWORK_OPTIONS = [
  { value: TargetFramework.SBTi, label: 'Science Based Targets (SBTi)' },
  { value: TargetFramework.NetZero, label: 'Net Zero' },
  { value: TargetFramework.CarbonNeutral, label: 'Carbon Neutral' },
  { value: TargetFramework.Custom, label: 'Custom' },
];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 11 }, (_, i) => currentYear + i);
const TARGET_YEAR_OPTIONS = Array.from({ length: 31 }, (_, i) => currentYear + i);

export const AdvancedTab = ({ formData, onFormChange }: AdvancedTabProps) => {
  return (
    <div>
      {/* Target Name */}
      <FormGroup>
        <Label className='fw-medium'>Target Name (Optional)</Label>
        <Input
          type='text'
          placeholder='e.g., Near-term SBTi Target 2030'
          value={formData.name || ''}
          onChange={(e) => onFormChange({ name: e.target.value })}
        />
        <small className='text-ThemeTextLight'>Give your target a descriptive name</small>
      </FormGroup>

      {/* Framework Selection */}
      <FormGroup className='mt-3'>
        <Label className='fw-medium'>Target Framework</Label>
        <Input
          type='select'
          value={formData.framework}
          onChange={(e) => onFormChange({ framework: e.target.value as TargetFramework })}
        >
          {FRAMEWORK_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Input>
      </FormGroup>

      {/* Reduction Percentage - Precise Input */}
      <FormGroup className='mt-3'>
        <Label className='fw-medium'>Reduction Percentage</Label>
        <div className='input-group'>
          <Input
            type='number'
            min='1'
            max='100'
            step='1'
            value={formData.reductionPercentage}
            onChange={(e) => onFormChange({ reductionPercentage: Number(e.target.value) })}
          />
          <span className='input-group-text'>%</span>
        </div>
        <small className='text-ThemeTextLight'>
          Percentage reduction from baseline year emissions
        </small>
      </FormGroup>

      {/* Year Selection */}
      <Row className='mt-3'>
        <Col md={6}>
          <FormGroup>
            <Label className='fw-medium'>Baseline Year</Label>
            <Input
              type='select'
              value={formData.baselineYear}
              onChange={(e) => onFormChange({ baselineYear: Number(e.target.value) })}
            >
              {YEAR_OPTIONS.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label className='fw-medium'>Target Year</Label>
            <Input
              type='select'
              value={formData.targetYear}
              onChange={(e) => onFormChange({ targetYear: Number(e.target.value) })}
            >
              {TARGET_YEAR_OPTIONS.filter((year) => year > formData.baselineYear).map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
      </Row>

      {/* Notes */}
      <FormGroup className='mt-3'>
        <Label className='fw-medium'>Notes (Optional)</Label>
        <Input
          type='textarea'
          rows={3}
          placeholder='Add any additional context or methodology notes...'
          value={formData.notes || ''}
          onChange={(e) => onFormChange({ notes: e.target.value })}
        />
      </FormGroup>
    </div>
  );
};
